import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /**
   * When false, Escape and backdrop-click do NOT close the dialog — the
   * caller must provide its own explicit exit control. Default true
   * (existing behavior, unchanged for every other caller). Added for the
   * 2FA recovery-codes step (fix-round item 7): those codes are shown
   * exactly once, so a stray Escape/backdrop tap must not be able to lose
   * them after the account is already enrolled server-side.
   */
  dismissable?: boolean;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(4, 5, 12, 0.82);
  backdrop-filter: blur(4px);
  ${media.mobile`
    align-items: flex-end;
    padding: 0;
  `}
`;

const Surface = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.color.surfaceRaised};
  background-image: ${({ theme }) => theme.surfaceGradients.overlay};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.elevation.md};
  padding: 20px;
  max-height: calc(100dvh - 40px);
  overflow-y: auto;
  ${media.mobile`
    max-width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    max-height: 90dvh;
    margin: 0;
  `}
`;

const Title = styled.h2`
  margin: 0 0 12px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.color.textPrimary};
`;

export function Dialog({ open, onClose, title, children, dismissable = true }: DialogProps) {
  const titleId = useId();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  // Refs, not useEffect dependencies: both `dismissable` and `onClose` can
  // change WHILE the dialog stays open without that being a reason to redo
  // the FOCUS-MANAGEMENT effect's setup/teardown (refocus-previous-element
  // on cleanup, refocus-surface on setup).
  //   - `dismissable` changes on purpose — stepping from 'qr' to the
  //     non-dismissable 'recovery' step without closing/reopening — and
  //     must take effect immediately, but re-running the effect for it
  //     would cause a focus flicker unrelated to dismissal.
  //   - `onClose` changes by ACCIDENT on nearly every caller: it's a
  //     function declared in the component body (e.g. TwoFactorDialog's
  //     `close()`), so it's a new reference on every parent re-render —
  //     including the re-render triggered by typing a single character
  //     into a field inside the dialog. If the effect depended on it
  //     directly, that keystroke would tear down and rebuild the effect,
  //     stealing focus back to `prevFocus.current` and then to the surface,
  //     kicking focus out of whatever input the member was just typing in.
  // Both are written in their own effect (never during render — refs are
  // not render-safe writes) so they stay current independent of the
  // focus-management effect below, which depends on neither.
  const dismissableRef = useRef(dismissable);
  useEffect(() => {
    dismissableRef.current = dismissable;
  }, [dismissable]);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    prevFocus.current = document.activeElement as HTMLElement;
    const surface = surfaceRef.current;
    surface?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (dismissableRef.current) onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && surface) {
        const focusables = surface.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        const withinList = Array.prototype.includes.call(focusables, active);
        if (!withinList) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      prevFocus.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <Backdrop
      data-testid="dialog-backdrop"
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <Surface
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <Title id={titleId}>{title}</Title>
        {children}
      </Surface>
    </Backdrop>,
    document.body,
  );
}
